/**
 * La file de validation ouverte — « À valider — 5 items ».
 *
 * On n'y revient que si on la demande : le traitement nominal enchaîne les
 * items sans repasser par une liste (§10.5, « traitement en file, pas en
 * page »). Cet écran sert deux choses seulement : voir ce qui reste, et savoir
 * si on a le temps de le vider — d'où la DURÉE annoncée par item et au total,
 * qui est ce qui rend la file crédible.
 *
 * Aucun pourcentage : le palier proposé est écrit en toutes lettres, et la
 * confiance du moteur est qualifiée en mots, jamais en nombre.
 */
import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { denombrer, type EvaluationAValiderDto } from '@silex/shared';

import { api } from '../api/client';
import { EmptyState } from '../components/EmptyState';
import { Chargement, ErreurRessource } from '../components/EtatRessource';
import {
  formatDateHeure,
  formatDuree,
  formatDureeTotale,
  initiale,
  libelleConfiance,
  libellePalier,
  libelleNiveauScolaire,
} from '../lib/tuteur-format';
import { useAsync } from '../lib/useAsync';

export function ValidationsPage() {
  const loader = useMemo(() => () => api.getEvaluationsAValider(), []);
  const file = useAsync(loader);
  /**
   * LOT 3.5 (CON-TUT-RAP-07) — la file, cadrée sur UN élève quand on y arrive
   * depuis son rapport.
   *
   * « "Corriger un palier de maîtrise" ouvre la file de validation GÉNÉRALE,
   * sans filtre sur l'élève du rapport, et sans le savoir visé. Ce n'est
   * "l'endroit où l'on corrige un palier" que si cet élève a précisément une
   * évaluation en attente. »
   *
   * Le filtre est CLIENT, et c'est suffisant : la file d'un tuteur porte ses
   * élèves et rien d'autre (le serveur l'a déjà cadrée sur ses affectations),
   * elle tient sur un écran, et rien de ce qui est masqué ici n'est caché à
   * quelqu'un — l'écran le dit et offre de retirer le filtre.
   */
  const [params, setParams] = useSearchParams();
  const eleveFiltre = params.get('eleveId');
  const toutes = file.status === 'ready' ? file.data.evaluations : [];
  const evaluations = eleveFiltre ? toutes.filter((e) => e.eleveId === eleveFiltre) : toutes;
  const prenomFiltre = eleveFiltre
    ? (toutes.find((e) => e.eleveId === eleveFiltre)?.elevePrenom ?? null)
    : null;
  const retirerFiltre = (): void => {
    const suivant = new URLSearchParams(params);
    suivant.delete('eleveId');
    setParams(suivant);
  };

  return (
    <div className="content__inner">
      <div className="dash-topbar">
        <div>
          <h1>
            {/* LOT 3.2 (CON-TUT-VAL-01) — « items » est un anglicisme, et il
                restait au pluriel à 1 (« 0 items », « 1 items »). */}
            {file.status === 'ready'
              ? `À valider — ${denombrer(file.data.nbEvaluations, 'élément')}`
              : 'À valider'}
          </h1>
          <p className="page-sub">
            {file.status === 'ready' && file.data.nbEvaluations > 0
              ? formatDureeTotale(file.data.dureeTotaleEstimeeMinutes)
              : "Les verdicts produits par l'IA qui attendent une décision humaine."}
          </p>
        </div>
        {file.status === 'ready' && file.data.nbEvaluations > 0 ? (
          <Link
            className="btn btn--primary btn--sm"
            to={`/validation/${file.data.evaluations[0]?.evaluationId ?? ''}`}
          >
            Tout enchaîner
          </Link>
        ) : null}
      </div>

      {file.status === 'loading' ? <Chargement quoi="la file de validation" /> : null}
      {file.status === 'error' ? <ErreurRessource message={file.message} /> : null}

      {file.status === 'ready' && file.data.nbEvaluations === 0 ? (
        <EmptyState title="Aucune évaluation n'attend de verdict">
          C'est un bon écran, pas un écran vide : rien n'est en attente d'un humain. Ce qui demande
          une décision aujourd'hui est dans « Ma journée ».
        </EmptyState>
      ) : null}

      {file.status === 'ready' && file.data.nbEvaluations > 0 && eleveFiltre ? (
        <p className="note-line" role="status">
          {evaluations.length > 0
            ? `Filtré sur ${prenomFiltre ?? 'cet élève'}.`
            : `Aucune évaluation n'attend de verdict pour ${prenomFiltre ?? 'cet élève'} — un palier ne se corrige que sur une évaluation en attente.`}{' '}
          <button type="button" className="btn btn--quiet btn--sm" onClick={retirerFiltre}>
            Voir toute la file
          </button>
        </p>
      ) : null}

      {file.status === 'ready' && evaluations.length > 0 ? (
        <>
          <div className="list">
            {evaluations.map((ev) => (
              <LigneEvaluation key={ev.evaluationId} evaluation={ev} />
            ))}
          </div>
          <p className="note-line">
            La durée annoncée par item est ce qui rend la file crédible : on sait avant de commencer
            si on a le temps de la vider. Une évaluation revue quitte la file d'elle-même — elle est
            recalculée à chaque ouverture, jamais mémorisée par cet écran.
          </p>
        </>
      ) : null}
    </div>
  );
}

function LigneEvaluation({ evaluation: ev }: { evaluation: EvaluationAValiderDto }) {
  return (
    <Link className="li li--link" to={`/validation/${ev.evaluationId}`}>
      <span className="avatar-sq avatar-sq--sm" aria-hidden="true">
        {initiale(ev.elevePrenom)}
      </span>
      <span className="li-main">
        <span className="li-title">
          {ev.elevePrenom} · {ev.savoir ? ev.savoir.enonce : 'savoir non résolu'}
        </span>
        <span className="li-sub">
          {ev.savoir ? `${ev.savoir.code} · ` : ''}
          {libelleNiveauScolaire(ev.eleveNiveauScolaire)} ·{' '}
          {ev.niveauMaitrisePropose
            ? `l'IA propose « ${libellePalier(ev.niveauMaitrisePropose).toLocaleLowerCase('fr')} »`
            : "l'IA ne propose aucun palier"}
          {ev.confiance ? ` · ${libelleConfiance(ev.confiance).toLocaleLowerCase('fr')}` : ''} ·
          soumise le {formatDateHeure(ev.soumiseLe)}
        </span>
      </span>
      <span className="chip-time">{formatDuree(ev.dureeEstimeeMinutes)}</span>
    </Link>
  );
}
